// frontend/src/components/Search/ConditionRow.jsx
// Single condition row for advanced search builder

import { Box, TextField, Select, MenuItem, IconButton, FormControl } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { formatHint } from '../../utils/syntaxHint';

/**
 * Operator options for condition
 */
const OPERATORS = [
  { value: 'AND', label: 'AND', color: 'primary.main' },
  { value: 'OR', label: 'OR', color: 'secondary.main' },
  { value: 'NOT', label: 'NOT', color: 'error.main' },
];

/**
 * Field options for per-condition field selector (Phase 1B)
 *
 * UI abstract names ('title' | 'content' | 'all'); backend
 * `_resolveFieldColumns` is the only translation point to FTS5 real columns.
 */
const FIELDS = [
  { value: 'title', label: '標題' },
  { value: 'content', label: '內容' },
  { value: 'all', label: '全部' },
];

/**
 * ConditionRow - Single condition row in advanced search
 *
 * @param {Object} condition - Condition object { id, operator, keyword, field }
 * @param {Function} onChange - Callback when condition changes
 * @param {Function} onRemove - Callback to remove this condition
 * @param {boolean} isFirst - Whether this is the first condition (no operator)
 * @param {boolean} showRemove - Whether to show remove button
 * @param {boolean} disabled - Whether inputs are disabled
 */
const ConditionRow = ({
  condition,
  onChange,
  onRemove,
  isFirst = false,
  showRemove = true,
  disabled = false,
}) => {
  const { operator, keyword, field = 'all' } = condition;
  const hint = formatHint(keyword);

  /**
   * Handle operator change
   */
  const handleOperatorChange = (e) => {
    onChange({
      ...condition,
      operator: e.target.value,
    });
  };

  /**
   * Handle keyword change
   */
  const handleKeywordChange = (e) => {
    onChange({
      ...condition,
      keyword: e.target.value,
    });
  };

  /**
   * Handle field change (Phase 1B)
   */
  const handleFieldChange = (e) => {
    onChange({
      ...condition,
      field: e.target.value,
    });
  };

  /**
   * Handle Enter key to prevent form submission
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 1,
      }}
    >
      {/* Operator selector (hidden for first row) */}
      <FormControl size="small" sx={{ minWidth: 80 }}>
        {isFirst ? (
          <Box
            sx={{
              py: 1,
              px: 1.5,
              backgroundColor: 'grey.100',
              borderRadius: 1,
              textAlign: 'center',
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            主詞
          </Box>
        ) : (
          <Select
            value={operator}
            onChange={handleOperatorChange}
            disabled={disabled}
            data-testid="condition-operator"
            sx={{
              '& .MuiSelect-select': {
                py: 1,
                fontWeight: 500,
              },
            }}
          >
            {OPERATORS.map((op) => (
              <MenuItem key={op.value} value={op.value} sx={{ color: op.color }}>
                {op.label}
              </MenuItem>
            ))}
          </Select>
        )}
      </FormControl>

      {/* Field selector (Phase 1B) */}
      <FormControl size="small" sx={{ minWidth: 90 }} data-testid="condition-field">
        <Select
          value={field}
          onChange={handleFieldChange}
          disabled={disabled}
          sx={{
            '& .MuiSelect-select': {
              py: 1,
            },
          }}
        >
          {FIELDS.map((f) => (
            <MenuItem key={f.value} value={f.value}>
              {f.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Keyword input */}
      <TextField
        size="small"
        placeholder={isFirst ? '輸入主要關鍵字...' : '輸入關鍵字...'}
        value={keyword}
        onChange={handleKeywordChange}
        onKeyPress={handleKeyPress}
        disabled={disabled}
        sx={{ flex: 1 }}
        autoComplete="off"
        helperText={hint.text}
        FormHelperTextProps={{ sx: { color: hint.color, mx: 0 } }}
      />

      {/* Remove button */}
      {showRemove && (
        <IconButton
          size="small"
          onClick={onRemove}
          disabled={disabled}
          data-testid="condition-remove"
          sx={{
            color: 'error.main',
            '&:hover': {
              backgroundColor: 'error.lighter',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}

      {/* Placeholder for alignment when remove button is hidden */}
      {!showRemove && <Box sx={{ width: 32 }} />}
    </Box>
  );
};

export default ConditionRow;
